import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Narrative {
  narrative_id: number;
  name: string;
  description?: string;
  user_prompt: string;
  system_prompt?: string;
  model_name: string;
  classifier_type: string;
  narrative_type?: string;
  version: string;
}

interface NarrativeFormData {
  name: string;
  description?: string;
  user_prompt: string;
  system_prompt?: string;
  model_name: string;
  classifier_type: string;
  narrative_type?: string;
}

interface NarrativeModalProps {
  narrative?: Narrative;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: NarrativeFormData) => void;
  isLoading: boolean;
}

const NarrativeModal: React.FC<NarrativeModalProps> = ({
  narrative,
  isOpen,
  onClose,
  onSave,
  isLoading,
}) => {
  const [formData, setFormData] = useState<NarrativeFormData>(() => ({
    name: narrative?.name || '',
    description: narrative?.description || '',
    user_prompt: narrative?.user_prompt || '',
    system_prompt: narrative?.system_prompt || '',
    model_name: narrative?.model_name || 'gemini-2.0-flash',
    classifier_type: narrative?.classifier_type || 'gemini_classifier',
    narrative_type: narrative?.narrative_type || '',
  }));

  React.useEffect(() => {
    if (narrative) {
      setFormData({
        name: narrative.name,
        description: narrative.description || '',
        user_prompt: narrative.user_prompt,
        system_prompt: narrative.system_prompt || '',
        model_name: narrative.model_name,
        classifier_type: narrative.classifier_type,
        narrative_type: narrative.narrative_type || '',
      });
    }
  }, [narrative]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content large-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{narrative ? 'Edit Narrative' : 'Create New Narrative'}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="narrative-form">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder="Enter narrative name..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter narrative description..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="classifier_type">Classifier Type *</label>
            <select
              id="classifier_type"
              value={formData.classifier_type}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  classifier_type: e.target.value,
                }))
              }
              required
            >
              <option value="gemini_classifier">gemini_classifier</option>
              <option value="gemini_video_classifier">
                gemini_video_classifier
              </option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="model_name">Model Name *</label>
            <input
              id="model_name"
              type="text"
              list="model_suggestions"
              value={formData.model_name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  model_name: e.target.value,
                }))
              }
              required
              placeholder="Enter or select model name..."
            />
            <datalist id="model_suggestions">
              <option value="gemini-3.1-pro-preview" />
              <option value="gemini-3-flash-preview" />
              <option value="gemini-3.1-flash-lite-preview" />
            </datalist>
          </div>

          <div className="form-group">
            <label htmlFor="narrative_type">Narrative Type</label>
            <select
              id="narrative_type"
              value={formData.narrative_type || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  narrative_type: e.target.value || undefined,
                }))
              }
            >
              <option value="">-- Select Type (Optional) --</option>
              <option value="related">Related</option>
              <option value="sentiment">Sentiment</option>
              <option value="stance">Stance</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="system_prompt">System Prompt</label>
            <textarea
              id="system_prompt"
              value={formData.system_prompt}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  system_prompt: e.target.value,
                }))
              }
              placeholder="Enter system prompt (optional)..."
              rows={5}
            />
          </div>

          <div className="form-group">
            <label htmlFor="user_prompt">User Prompt *</label>
            <textarea
              id="user_prompt"
              value={formData.user_prompt}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  user_prompt: e.target.value,
                }))
              }
              required
              placeholder="Enter user prompt..."
              rows={10}
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading
                ? 'Saving...'
                : narrative
                  ? 'Update Narrative'
                  : 'Create Narrative'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const NarrativeManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedNarrative, setSelectedNarrative] = useState<
    Narrative | undefined
  >();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedNarrative, setExpandedNarrative] = useState<number | null>(
    null,
  );

  const {
    data: narratives = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Narrative[]>({
    queryKey: ['narratives'],
    queryFn: async () => {
      const response = await fetch('/api/narratives');
      if (!response.ok) throw new Error('Failed to fetch narratives');
      return response.json();
    },
    refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: async (data: NarrativeFormData) => {
      const response = await fetch('/api/narratives', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create narrative');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedNarrative(undefined);
      queryClient.invalidateQueries({ queryKey: ['narratives'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: NarrativeFormData;
    }) => {
      const response = await fetch(`/api/narratives/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update narrative');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedNarrative(undefined);
      queryClient.invalidateQueries({ queryKey: ['narratives'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/narratives/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete narrative');
      }
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['narratives'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const handleCreate = useCallback(() => {
    setSelectedNarrative(undefined);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEdit = useCallback((narrative: Narrative) => {
    setSelectedNarrative(narrative);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleDelete = useCallback(
    (narrative: Narrative) => {
      if (
        window.confirm(
          `Are you sure you want to delete the narrative "${narrative.name}"?\n\nThis action cannot be undone.`,
        )
      ) {
        deleteMutation.mutate(narrative.narrative_id);
      }
    },
    [deleteMutation],
  );

  const handleSave = useCallback(
    (data: NarrativeFormData) => {
      if (selectedNarrative) {
        updateMutation.mutate({ id: selectedNarrative.narrative_id, data });
      } else {
        createMutation.mutate(data);
      }
    },
    [selectedNarrative, createMutation, updateMutation],
  );

  const toggleExpanded = (id: number) => {
    setExpandedNarrative(expandedNarrative === id ? null : id);
  };

  if (isLoading) return <div className="loading">Loading narratives...</div>;
  if (error)
    return (
      <div className="error">
        Error loading narratives: {(error as Error).message}
      </div>
    );

  return (
    <div className="narrative-manager">
      <div className="page-header">
        <h1>📖 Narrative Manager</h1>
        <div className="page-actions">
          <button onClick={() => refetch()} className="btn-secondary">
            🔄 Refresh
          </button>
          <button onClick={handleCreate} className="btn-primary">
            ➕ Add Narrative
          </button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>⚠️ {actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div className="narratives-list">
        {narratives.map((narrative) => (
          <div key={narrative.narrative_id} className="narrative-card">
            <div className="narrative-header">
              <div className="narrative-title">
                <h3>{narrative.name}</h3>
                <span className="narrative-badge">
                  ID: {narrative.narrative_id}
                </span>
                <span className="narrative-badge version">
                  v{narrative.version}
                </span>
                <span className="narrative-badge model">
                  {narrative.model_name}
                </span>
                {narrative.narrative_type && (
                  <span className="narrative-badge type">
                    {narrative.narrative_type}
                  </span>
                )}
              </div>
              <div className="narrative-actions">
                <button
                  onClick={() => toggleExpanded(narrative.narrative_id)}
                  className="btn-icon"
                  title={
                    expandedNarrative === narrative.narrative_id
                      ? 'Collapse'
                      : 'Expand'
                  }
                >
                  {expandedNarrative === narrative.narrative_id ? '🔼' : '🔽'}
                </button>
                <button
                  onClick={() => handleEdit(narrative)}
                  className="btn-icon"
                  title="Edit narrative"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(narrative)}
                  className="btn-icon btn-danger"
                  title="Delete narrative"
                >
                  🗑️
                </button>
              </div>
            </div>

            {narrative.description && (
              <div className="narrative-description">
                <p>{narrative.description}</p>
              </div>
            )}

            {expandedNarrative === narrative.narrative_id && (
              <div className="narrative-details">
                <div className="narrative-detail-section">
                  <strong>Classifier Type:</strong>
                  <code>{narrative.classifier_type}</code>
                </div>

                {narrative.narrative_type && (
                  <div className="narrative-detail-section">
                    <strong>Narrative Type:</strong>
                    <code>{narrative.narrative_type}</code>
                  </div>
                )}

                {narrative.system_prompt && (
                  <div className="narrative-detail-section">
                    <strong>System Prompt:</strong>
                    <pre className="prompt-text">{narrative.system_prompt}</pre>
                  </div>
                )}

                <div className="narrative-detail-section">
                  <strong>User Prompt:</strong>
                  <pre className="prompt-text">{narrative.user_prompt}</pre>
                </div>
              </div>
            )}
          </div>
        ))}

        {narratives.length === 0 && (
          <div className="empty-state">
            <h3>No narratives yet</h3>
            <p>Create your first narrative to start classifying videos.</p>
            <button onClick={handleCreate} className="btn-primary">
              ➕ Create First Narrative
            </button>
          </div>
        )}
      </div>

      <NarrativeModal
        narrative={selectedNarrative}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
};

export default NarrativeManager;
